// src/components/NotificationCenter.tsx
import React from 'react';
import { Badge, Dropdown, List, Button, Empty, Typography } from 'antd';
import { BellOutlined, CheckOutlined, DeleteOutlined, InfoCircleOutlined, WarningOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useNotificationStore } from '../store/useNotificationStore';
import type { MenuProps } from 'antd';

const { Text } = Typography;

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.currentUser);
  const { notifications, markAsRead, markAllAsRead, deleteNotification } = useNotificationStore();

  const userNotifications = currentUser 
    ? notifications.filter(n => n.userId === currentUser.id)
    : [];

  const unreadCount = userNotifications.filter(n => !n.read).length;

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const notificationContent = (
    <div style={{ 
      width: 380, 
      maxHeight: 500, 
      overflow: 'auto',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
    }}>
      <div style={{ 
        padding: '12px 16px', 
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#fff'
      }}>
        <Text strong>Notificaciones</Text>
        {unreadCount > 0 && (
          <Button 
            type="link" 
            size="small" 
            icon={<CheckOutlined />}
            onClick={markAllAsRead}
          >
            Marcar todas como leídas
          </Button>
        )}
      </div>

      {userNotifications.length === 0 ? (
        <Empty 
          description="No hay notificaciones" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '40px 0', backgroundColor: '#fff' }}
        />
      ) : (
        <List
          dataSource={userNotifications}
          style={{ backgroundColor: '#fff' }}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: item.read ? '#fff' : '#f0f7ff',
                borderLeft: item.read ? 'none' : '3px solid #1890ff',
                transition: 'background-color 0.3s',
              }}
              onClick={() => handleNotificationClick(item)}
              actions={[
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNotification(item.id);
                  }}
                />
              ]}
            >
              <List.Item.Meta
                avatar={getIcon(item.type)}
                title={
                  <Text strong={!item.read}>{item.title}</Text>
                }
                description={
                  <>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.message}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {new Date(item.createdAt).toLocaleString('es-ES')}
                    </Text>
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => notificationContent}
      trigger={['click']}
      placement="bottomRight"
    >
      <Badge count={unreadCount} offset={[-5, 5]}>
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 18 }} />}
          style={{ marginRight: 16 }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationCenter;
